import React, { createContext, useState, useEffect, useContext } from 'react';
import { getToken, getUserData, saveUserData } from '../utils/storage';
import * as authService from '../services/authService';
import { getUserProfile } from '../services/userService';

const AuthContext = createContext({});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Beim Start: Token und User-Daten aus Storage laden
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const token = await getToken();
      const userData = await getUserData();

      if (token && userData) {
        // Lade immer die aktuellen User-Daten mit Präferenzen vom Server
        const profileResult = await getUserProfile();
        if (profileResult.success) {
          setUser(profileResult.data);
          // Aktualisiere auch die gespeicherten User-Daten
          await saveUserData(profileResult.data);
        } else {
          setUser(userData);
        }
        setIsAuthenticated(true);
      }
    } catch (error) {
      console.error('Fehler beim Prüfen der Authentifizierung:', error);
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (email, password) => {
    const result = await authService.login(email, password);
    
    if (result.success) {
      setUser(result.user);
      setIsAuthenticated(true);
    }
    
    return result;
  };

  const signUp = async (email, password, firstName, lastName, familyName, invitationCode) => {
    const result = await authService.register(email, password, firstName, lastName, familyName, invitationCode);
    
    if (result.success) {
      setUser(result.user);
      setIsAuthenticated(true);
    }
    
    return result;
  };

  const signOut = async () => {
    const result = await authService.logout();
    
    if (result.success) {
      setUser(null);
      setIsAuthenticated(false);
    }
    
    return result;
  };

  const updatePreferences = async (preferences) => {
    try {
      const result = await authService.updatePreferences(preferences);
      if (result.success) {
        // Aktualisiere User-Daten mit neuen Präferenzen
        const profileResult = await getUserProfile();
        if (profileResult.success) {
          setUser(profileResult.data);
          // Aktualisiere auch die gespeicherten User-Daten
          await saveUserData(profileResult.data);
        }
      }
      return result;
    } catch (error) {
      console.error('Fehler beim Aktualisieren der Präferenzen:', error);
      return { success: false, error: 'Fehler beim Aktualisieren der Präferenzen' };
    }
  };

  const value = {
    user,
    isAuthenticated,
    loading,
    signIn,
    signUp,
    signOut,
    updatePreferences,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth muss innerhalb eines AuthProvider verwendet werden');
  }
  return context;
};

export default AuthContext;

